import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const PAGE_SIZE = 10;

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    plus: 'M12 5v14 M5 12h14',
    search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
    edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 00 2 2h14a2 2 0 00 2-2v-7 M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
    trash: 'M3 6h18 M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2 M10 11v6 M14 11v6',
    x: 'M18 6L6 18 M6 6l12 12',
    truck: 'M1 3h15v13H1V3zm15 8h4l3 3v2h-7v-5zM5 18a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm12 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
    building: 'M3 21h18M3 7v14m18-14v14M3 7l9-4 9 4M5 21V11m4 10V11m6 10V11m4 10V11'
};

const defaultVehicle = {
    vendorId: '',
    vehicleCode: '',
    vehicleType: '',
    registrationNumber: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    fuelType: '',
    color: '',
    purchasePrice: '',
    acquisitionDate: '',
    depreciationMonthly: '',
    gpsMonthly: '',
    dashcamMonthly: '',
    insuranceMonthly: '',
    otherCostsMonthly: '',
    efficiency: '',
    status: 'Active',
    seatingCapacity: '',
    insuranceExpiry: '',
    registrationExpiry: '',
    vehicleLicenseNumber: '',
    vehicleLicenseExpiry: '',
    fuelStickerNumber: '',
    insuranceProvider: '',
    policyNumber: '',
    fleetReceivedDate: '',
    photo: '',
    documents: []
};

export default function VendorFleet({ user }) {
    const [fleet, setFleet] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [vehicleMakes, setVehicleMakes] = useState([]);
    const [vehicleModels, setVehicleModels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [vendorFilter, setVendorFilter] = useState('');
    const [showModal, setShowModal] = useState(false);
        const [form, setForm] = useState(defaultVehicle);
    const [documentsList, setDocumentsList] = useState([{ name: '', file: null }]);
    const [editingId, setEditingId] = useState(null);
    const [originalStatus, setOriginalStatus] = useState('Active');
    const [toast, setToast] = useState(null);
    const [showImportScreen, setShowImportScreen] = useState(false);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const handleAddDocument = () => setDocumentsList(prev => [...prev, { name: '', file: null }]);
    
    const handleDocumentChange = (index, field, value) => {
        const updated = [...documentsList];
        updated[index][field] = value;
        setDocumentsList(updated);
    };

    const handleRemoveDocument = (index) => {
        const updated = [...documentsList];
        updated.splice(index, 1);
        setDocumentsList(updated);
    };

    const fetchFleet = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`/api/vendor-fleet/${user?.operatorId || 'all'}`);
            setFleet(res.data);
        } catch (err) {
            console.error('Fetch fleet error:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchVendors = async () => {
        try {
            const res = await axios.get(`/api/vendors/${user?.operatorId || 'all'}`);
            setVendors(res.data);
        } catch (err) {
            console.error('Fetch vendors error:', err);
        }
    };

    const fetchVehicleTypes = async () => {
        try {
            const res = await axios.get('/api/master-data');
            const all = res.data;
            setVehicleTypes(all.filter(i => i.category === 'vehicle-types' && i.active));
            setVehicleMakes(all.filter(i => i.category === 'vehicle-makes' && i.active));
            setVehicleModels(all.filter(i => i.category === 'vehicle-models' && i.active));
        } catch (err) {
            console.error('Fetch vehicle types error:', err);
        }
    };

    useEffect(() => {
        fetchFleet();
        fetchVendors();
        fetchVehicleTypes();
    }, [user]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return fleet.filter(f => {
            const vendorMatch = !vendorFilter || (f.vendorId?._id || f.vendorId) === vendorFilter;
            const searchMatch = !q ||
                (f.vendorId?.vendorName || '').toLowerCase().includes(q) ||
                f.registrationNumber.toLowerCase().includes(q) ||
                f.make.toLowerCase().includes(q) ||
                f.model.toLowerCase().includes(q) ||
                f.vehicleType.toLowerCase().includes(q);
            return vendorMatch && searchMatch;
        });
    }, [fleet, search, vendorFilter]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageData = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = { ...form, operatorId: user?.operatorId || user?._id };
            const data = new FormData();
            const skipKeys = new Set(['_id', '__v', 'documents', 'createdAt', 'updatedAt']);
            
            Object.keys(payload).forEach(key => {
                if (skipKeys.has(key)) return;
                const val = payload[key];
                if (val !== undefined && val !== null) {
                    data.append(key, val);
                }
            });
            
            data.append('existingDocuments', JSON.stringify(form.documents || []));
            
            const documentNames = [];
            documentsList.forEach(doc => {
                if (doc.file) {
                    data.append('documents', doc.file);
                    documentNames.push(doc.name || 'Document');
                }
            });
            if (documentNames.length > 0) {
                data.append('documentNames', JSON.stringify(documentNames));
            }

            if (editingId) {
                await axios.put(`/api/vendor-fleet/${editingId}`, data);
                showToast('Vehicle updated successfully');
            } else {
                await axios.post('/api/vendor-fleet', data);
                showToast('Vehicle added successfully');
            }
            fetchFleet();
            closeModal();
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to save vehicle';
            showToast(msg, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (vehicle) => {
        if (!window.confirm(`Delete vehicle "${vehicle.registrationNumber}"?`)) return;
        try {
            await axios.delete(`/api/vendor-fleet/${vehicle._id}`);
            showToast('Vehicle deleted successfully');
            fetchFleet();
        } catch (err) {
            showToast('Failed to delete vehicle', 'error');
        }
    };

    const openModal = (vehicle = null) => {
        setDocumentsList([{ name: '', file: null }]);
        if (vehicle) {
            setEditingId(vehicle._id);
            setOriginalStatus(vehicle.status || 'Active');
            setForm({
                vendorId: vehicle.vendorId?._id || vehicle.vendorId || '',
                vehicleCode: vehicle.vehicleCode || '',
                vehicleType: vehicle.vehicleType || '',
                registrationNumber: vehicle.registrationNumber || '',
                make: vehicle.make || '',
                model: vehicle.model || '',
                year: vehicle.year || new Date().getFullYear(),
                fuelType: vehicle.fuelType || '',
                color: vehicle.color || '',
                purchasePrice: vehicle.purchasePrice || '',
                acquisitionDate: vehicle.acquisitionDate ? vehicle.acquisitionDate.slice(0, 10) : '',
                depreciationMonthly: vehicle.depreciationMonthly || '',
                gpsMonthly: vehicle.gpsMonthly || '',
                dashcamMonthly: vehicle.dashcamMonthly || '',
                insuranceMonthly: vehicle.insuranceMonthly || '',
                otherCostsMonthly: vehicle.otherCostsMonthly || '',
                efficiency: vehicle.efficiency || '',
                status: vehicle.status || 'Active',
                seatingCapacity: vehicle.seatingCapacity || '',
                insuranceExpiry: vehicle.insuranceExpiry ? vehicle.insuranceExpiry.split('T')[0] : '',
                registrationExpiry: vehicle.registrationExpiry ? vehicle.registrationExpiry.split('T')[0] : '',
                vehicleLicenseNumber: vehicle.vehicleLicenseNumber || '',
                vehicleLicenseExpiry: vehicle.vehicleLicenseExpiry ? vehicle.vehicleLicenseExpiry.split('T')[0] : '',
                fuelStickerNumber: vehicle.fuelStickerNumber || '',
                insuranceProvider: vehicle.insuranceProvider || '',
                policyNumber: vehicle.policyNumber || '',
                fleetReceivedDate: vehicle.fleetReceivedDate ? vehicle.fleetReceivedDate.split('T')[0] : '',
                photo: vehicle.photo || '',
                documents: vehicle.documents || []
            });
        } else {
            setEditingId(null);
            setOriginalStatus('Active');
            setForm(defaultVehicle);
        }
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingId(null);
        setForm(defaultVehicle);
    };

    if (showImportScreen) {
        return (
            <VendorFleetImportWizard
                operatorId={user?.operatorId || user?._id}
                vendors={vendors}
                onBack={() => setShowImportScreen(false)}
                onDone={() => { setShowImportScreen(false); fetchFleet(); }}
            />
        );
    }

    return (
        <div className="fleet-page">
            <style>{`
                .fleet-page { padding: 20px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .header-section { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
                .title-wrap h1 { font-size: 24px; font-weight: 700; color: #1e293b; margin: 0; }
                .add-btn { 
                    background: #00B2A9; color: white; border: none; padding: 10px 20px; 
                    border-radius: 8px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 8px;
                    transition: all 0.2s; box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
                }
                .add-btn:hover { background: #16539C; transform: translateY(-1px); }

                .vendor-filter-bar {
                    display: flex; align-items: center; gap: 14px; margin-bottom: 20px;
                    padding: 14px 20px; background: white; border-radius: 12px;
                    border: 1px solid #e2e8f0; box-shadow: 0 1px 4px rgba(0,0,0,0.03);
                }
                .vendor-filter-bar label {
                    font-size: 13px; font-weight: 600; color: #475569; white-space: nowrap;
                }
                .vendor-filter-select {
                    padding: 9px 14px; border: 1.5px solid #e2e8f0; border-radius: 8px;
                    font-size: 14px; color: #1e293b; background: #f8fafc; outline: none;
                    min-width: 240px; cursor: pointer; transition: all 0.2s;
                    font-family: 'Inter', sans-serif;
                }
                .vendor-filter-select:focus { border-color: #00B2A9; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }

                .content-box { 
                    background: white; border-radius: 16px; border: 1px solid #e2e8f0; 
                    box-shadow: 0 4px 20px rgba(0,0,0,0.03); padding: 24px;
                }
                
                .table-container { overflow-x: auto; }
                .fleet-table { width: 100%; border-collapse: collapse; }
                .fleet-table th { 
                    padding: 10px 16px; text-align: left; font-size: 12px; font-weight: 600; 
                    color: #64748b; text-transform: uppercase; letter-spacing: 0.05em;
                    border-bottom: 1px solid #e2e8f0; background: #fff;
                }
                .fleet-table td { padding: 10px 16px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; font-size: 14px; }
                .fleet-table tr:hover { background: #f8fafc; }
                
                .vendor-text { font-weight: 600; color: #1e293b; }
                .vehicle-info { display: flex; flex-direction: column; }
                .vehicle-main { font-weight: 600; color: #334155; }
                .vehicle-sub { font-size: 13px; color: #64748b; }

                .status-badge { 
                    padding: 5px 15px; border-radius: 99px; font-size: 12px; font-weight: 600;
                    display: inline-block;
                }
                .status-active { background: #f0fdf4; color: #166534; border: 1px solid #dcfce7; }
                .status-under-review { background: #fef9c3; color: #92400e; border: 1px solid #fef08a; }
                .status-inactive { background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; }
                .status-under-maintenance { background: #fff7ed; color: #ea580c; border: 1px solid #ffedd5; }
                .status-damaged { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
                .status-retired { background: #f8fafc; color: #64748b; border: 1px solid #e2e8f0; }
                .status-in-transit { background: #eff6ff; color: #2563eb; border: 1px solid #bfdbfe; }
                
                .actions-cell { display: flex; gap: 8px; }
                .act-btn { 
                    border: none; background: none; padding: 6px; cursor: pointer; border-radius: 6px;
                    color: #94a3b8; transition: all 0.2s;
                }
                .act-btn:hover { background: #f1f5f9; color: #00B2A9; }
                .act-btn.delete:hover { background: #fee2e2; color: #ef4444; }

                /* Modal */
                .modal-overlay { 
                    position: fixed; inset: 0; background: rgba(15, 23, 42, 0.6); 
                    backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 1000;
                    animation: fadeIn 0.3s ease;
                }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

                .modal-content { 
                    background: white; border-radius: 28px; width: 100%; max-width: 680px; 
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                    animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
                    overflow: hidden; border: 1px solid rgba(0, 0, 0, 0.05);
                    max-height: 92vh; display: flex; flex-direction: column;
                }
                @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

                .modal-header { 
                    padding: 24px 32px; border-bottom: 1px solid #f1f5f9; 
                    display: flex; justify-content: space-between; align-items: center;
                    background: #fff;
                }
                .modal-header h2 { font-size: 20px; font-weight: 800; color: #1e293b; margin: 0; letter-spacing: -0.02em; }
                .close-btn { 
                    border: none; background: #f8fafc; cursor: pointer; color: #64748b; padding: 8px; 
                    border-radius: 10px; transition: all 0.2s;
                }
                .close-btn:hover { color: #063A5C; background: #f1f5f9; transform: rotate(90deg); }

                .modal-body { padding: 32px; background: #fff; overflow-y: auto; flex: 1; }
                .section-label { font-size: 11px; font-weight: 800; color: #00B2A9; text-transform: uppercase; letter-spacing: 0.08em; margin: 24px 0 14px; padding-bottom: 6px; border-bottom: 1.5px solid #e6f0fa; }
                .section-label:first-child { margin-top: 0; }
                
                .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
                .form-group { display: flex; flex-direction: column; gap: 8px; margin-bottom: 20px; }
                .form-group.full { grid-column: span 2; }
                .form-label { font-size: 13px; font-weight: 700; color: #475569; display: flex; align-items: center; gap: 6px; }
                
                .input-wrapper { position: relative; display: flex; align-items: center; }
                .input-icon { position: absolute; left: 14px; color: #94a3b8; pointer-events: none; }
                
                .form-input { 
                    width: 100%; padding: 12px 16px 12px 42px; border: 2px solid #e2e8f0; border-radius: 14px; font-size: 14px; 
                    background: #fcfdfe; outline: none; transition: all 0.2s; font-family: 'Inter', sans-serif;
                    color: #1e293b; box-sizing: border-box; font-weight: 500;
                }
                .form-input:focus { 
                    border-color: #00B2A9; box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.08); 
                    background: #fff;
                }
                .form-input:focus + .input-icon { color: #00B2A9; }

                .modal-footer { 
                    padding: 24px 32px; border-top: 1px solid #f1f5f9; 
                    display: flex; justify-content: flex-end; gap: 14px; 
                    background: #fcfdfe;
                }
                .cancel-btn { 
                    padding: 12px 24px; border: 2px solid #e2e8f0; background: white; border-radius: 12px;
                    font-size: 14px; font-weight: 700; color: #64748b; cursor: pointer; transition: all 0.2s;
                }
                .cancel-btn:hover { border-color: #00B2A9; color: #00B2A9; background: #f0f7ff; }
                
                .submit-btn { 
                    padding: 12px 28px; border: none; background: #00B2A9; color: white; border-radius: 12px;
                    font-size: 14px; font-weight: 700; cursor: pointer; transition: all 0.2s;
                    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2);
                }
                .submit-btn:hover { background: #16539C; transform: translateY(-2px); box-shadow: 0 8px 20px rgba(99, 102, 241, 0.3); }

                .toast {
                    position: fixed; top: 24px; right: 24px; padding: 16px 28px; border-radius: 16px;
                    color: white; z-index: 2000; box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    font-weight: 700; font-size: 14px; animation: toastSlide 0.4s cubic-bezier(0.16, 1, 0.3, 1);
                }
                @keyframes toastSlide { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
                .toast.success { background: #10b981; }
                .toast.error { background: #ef4444; }
            `}</style>

            {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}

            <div className="header-section">
                <div className="title-wrap">
                    <h1>Vendor Fleet</h1>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                    <button className="add-btn" style={{ background: 'linear-gradient(135deg,#10b981,#059669)' }} onClick={() => setShowImportScreen(true)}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                        Import
                    </button>
                    <button className="add-btn" onClick={() => openModal()}>
                        <Icon d={icons.plus} size={18} strokeWidth={2.5} />
                        Add Vehicle
                    </button>
                </div>
            </div>

            <div className="vendor-filter-bar">
                <label>Filter by Vendor:</label>
                <select
                    className="vendor-filter-select"
                    value={vendorFilter}
                    onChange={e => { setVendorFilter(e.target.value); setPage(1); }}
                >
                    <option value="">All Vendors</option>
                    {vendors.map(v => (
                        <option key={v._id} value={v._id}>{v.vendorName}</option>
                    ))}
                </select>
            </div>

            <div className="content-box">
                <div className="table-container">
                    <table className="fleet-table">
                        <thead>
                            <tr>
                                <th>Vendor</th>
                                <th>Vehicle Info & Efficiency</th>
                                <th>Vehicle ID</th>
                                <th>Registration</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="6" style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>Loading fleet...</td></tr>
                            ) : pageData.length === 0 ? (
                                <tr><td colSpan="6" style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>No vehicles found.</td></tr>
                            ) : (
                                pageData.map(item => (
                                    <tr key={item._id}>
                                        <td>
                                            <div className="vendor-text">{item.vendorId?.vendorName || 'Deleted Vendor'}</div>
                                        </td>
                                        <td>
                                            <div className="vehicle-info">
                                                <span className="vehicle-main">{item.make} {item.model}</span>
                                                <span className="vehicle-sub">
                                                    ({item.vehicleType}) 
                                                    {item.efficiency && <span style={{ color: '#166534', fontWeight: 600, marginLeft: 6, background: '#dcfce7', padding: '2px 6px', borderRadius: 4 }}>⚡ {item.efficiency}</span>}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <span style={{ fontWeight: 700, color: '#16539C', background: '#eef2ff', padding: '6px 10px', borderRadius: '8px' }}>
                                                {item.vehicleCode || '-'}
                                            </span>
                                        </td>
                                        <td style={{ fontWeight: 500, color: '#334155' }}>
                                            {item.registrationNumber}
                                        </td>
                                        <td>
                                            <span className={`status-badge status-${item.status?.toLowerCase().replaceAll(' ', '-')}`}>
                                                {item.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="actions-cell">
                                                <button className="act-btn" title="Edit" onClick={() => openModal(item)}>
                                                    <Icon d={icons.edit} size={16} />
                                                </button>
                                                <button className="act-btn delete" title="Delete" onClick={() => handleDelete(item)}>
                                                    <Icon d={icons.trash} size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingId ? 'Edit Vehicle Details' : 'Register New Vehicle'}</h2>
                            <button className="close-btn" onClick={closeModal}><Icon d={icons.x} size={20} /></button>
                        </div>
                        <form onSubmit={handleSave} style={{ display: 'contents' }}>
                            <div className="modal-body">
                                {/* ── Section: Vendor & Identity ── */}
                                <div className="section-label">Vendor & Identification</div>
                                <div className="form-grid">
                                    <div className="form-group full">
                                        <label className="form-label">
                                            <Icon d={icons.building} size={14} />
                                            Vendor *
                                        </label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.building} size={16} className="input-icon" />
                                            <select className="form-input" required value={form.vendorId} onChange={e => setForm({ ...form, vendorId: e.target.value })}>
                                                <option value="">Select a vendor</option>
                                                {vendors.map(v => (
                                                    <option key={v._id} value={v._id}>{v.vendorName}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Vehicle ID / Code</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.truck} size={16} className="input-icon" />
                                            <input className="form-input" placeholder="e.g. V-001" value={form.vehicleCode} onChange={e => setForm({ ...form, vehicleCode: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Registration Number *</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.search} size={16} className="input-icon" />
                                            <input className="form-input" required placeholder="e.g. DXB-12345" value={form.registrationNumber} onChange={e => setForm({ ...form, registrationNumber: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Vehicle Type *</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.truck} size={16} className="input-icon" />
                                            <select className="form-input" required value={form.vehicleType} onChange={e => setForm({ ...form, vehicleType: e.target.value })}>
                                                <option value="">Select type</option>
                                                {vehicleTypes.map(vt => (
                                                    <option key={vt._id} value={vt.name}>{vt.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* ── Photo ── */}
                                <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
                                    <div style={{ width: 80, height: 80, borderRadius: 16, background: '#f1f5f9', border: '2px dashed #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
                                        {form.photo ? (
                                            <img src={form.photo} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <Icon d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" size={28} color="#94a3b8" />
                                        )}
                                        <input 
                                            type="file" accept="image/*"
                                            style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) {
                                                    const reader = new FileReader();
                                                    reader.onload = (evt) => setForm({ ...form, photo: evt.target.result });
                                                    reader.readAsDataURL(file);
                                                }
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <h4 style={{ margin: '0 0 4px', fontSize: 13, color: '#1e293b', fontWeight: 600 }}>Upload Photo</h4>
                                        <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>Click or drag image to upload.</p>
                                    </div>
                                </div>

                                {/* ── Section: Vehicle Details ── */}
                                <div className="section-label">Vehicle Details</div>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="form-label">Make *</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.truck} size={16} className="input-icon" style={{ opacity: 0.5 }} />
                                            <select className="form-input" required value={form.make} onChange={e => setForm({ ...form, make: e.target.value })}>
                                                <option value="">Select Make...</option>
                                                {vehicleMakes.map(m => <option key={m._id} value={m.name}>{m.name}</option>)}
                                                {form.make && !vehicleMakes.find(m => m.name === form.make) && <option value={form.make}>{form.make}</option>}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Model</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.truck} size={16} className="input-icon" style={{ opacity: 0.5 }} />
                                            <select className="form-input" value={form.model} onChange={e => setForm({ ...form, model: e.target.value })}>
                                                <option value="">Select Model...</option>
                                                {vehicleModels.map(m => <option key={m._id} value={m.name}>{m.name}</option>)}
                                                {form.model && !vehicleModels.find(m => m.name === form.model) && <option value={form.model}>{form.model}</option>}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Year</label>
                                        <div className="input-wrapper">
                                            <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" size={16} className="input-icon" />
                                            <input className="form-input" type="number" min="1990" max={new Date().getFullYear() + 1} placeholder={new Date().getFullYear()} value={form.year} onChange={e => setForm({ ...form, year: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Fuel Type</label>
                                        <div className="input-wrapper">
                                            <Icon d="M13 2a2 2 0 0 0-2 2v1H7a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-4V4a2 2 0 0 0-2-2z" size={16} className="input-icon" />
                                            <select className="form-input" value={form.fuelType} onChange={e => setForm({ ...form, fuelType: e.target.value })}>
                                                <option value="">Select fuel type</option>
                                                <option>Petrol</option>
                                                <option>Diesel</option>
                                                <option>Electric</option>
                                                <option>Hybrid</option>
                                                <option>CNG</option>
                                                <option>LPG</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Color</label>
                                        <div className="input-wrapper" style={{ gap: 10 }}>
                                            <input type="color" value={form.color || '#ffffff'} onChange={e => setForm({ ...form, color: e.target.value })} style={{ width: 42, height: 42, border: '2px solid #e2e8f0', borderRadius: 10, cursor: 'pointer', padding: 2, background: 'none' }} />
                                            <input className="form-input" style={{ paddingLeft: 16 }} placeholder="e.g. White" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Fleet Received Date</label>
                                        <div className="input-wrapper">
                                            <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" size={16} className="input-icon" />
                                            <input className="form-input" type="date" value={form.fleetReceivedDate} onChange={e => setForm({ ...form, fleetReceivedDate: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Seating Capacity</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.truck} size={16} className="input-icon" />
                                            <input className="form-input" type="number" placeholder="e.g. 15" value={form.seatingCapacity} onChange={e => setForm({ ...form, seatingCapacity: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Fuel Sticker Number</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.search} size={16} className="input-icon" />
                                            <input className="form-input" placeholder="e.g. FS-123" value={form.fuelStickerNumber} onChange={e => setForm({ ...form, fuelStickerNumber: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Efficiency</label>
                                        <div className="input-wrapper">
                                            <Icon d="M13 2a2 2 0 0 0-2 2v1H7a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-4V4a2 2 0 0 0-2-2z" size={16} className="input-icon" />
                                            <input className="form-input" placeholder="e.g. 15 km/l" value={form.efficiency} onChange={e => setForm({ ...form, efficiency: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Operational Status</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.truck} size={16} className="input-icon" style={{ color: form.status === 'Active' ? '#10b981' : '#94a3b8' }} />
                                            <select className="form-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                                                {originalStatus === 'Under Review' ? (
                                                    <>
                                                        <option value="Under Review">Under Review</option>
                                                        <option value="Active">Accept (Active)</option>
                                                        <option value="Inactive">Reject (Inactive)</option>
                                                    </>
                                                ) : (
                                                    <>
                                                        <option value="Active">Active</option>
                                                        <option value="Inactive">Inactive</option>
                                                        <option value="Under Maintenance">Under Maintenance</option>
                                                        <option value="Damaged">Damaged</option>
                                                        <option value="Retired">Retired</option>
                                                        <option value="In Transit">In Transit</option>
                                                    </>
                                                )}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* ── Section: Registration & Insurance ── */}
                                <div className="section-label">Registration & License</div>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="form-label">Registration Expiry Date</label>
                                        <div className="input-wrapper">
                                            <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" size={16} className="input-icon" />
                                            <input className="form-input" type="date" value={form.registrationExpiry} onChange={e => setForm({ ...form, registrationExpiry: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Vehicle License / Permit No.</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.search} size={16} className="input-icon" />
                                            <input className="form-input" placeholder="e.g. LIC-9988" value={form.vehicleLicenseNumber} onChange={e => setForm({ ...form, vehicleLicenseNumber: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">License Expiry Date</label>
                                        <div className="input-wrapper">
                                            <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" size={16} className="input-icon" />
                                            <input className="form-input" type="date" value={form.vehicleLicenseExpiry} onChange={e => setForm({ ...form, vehicleLicenseExpiry: e.target.value })} />
                                        </div>
                                    </div>
                                </div>

                                <div className="section-label">Insurance Details</div>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="form-label">Insurance Provider</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.search} size={16} className="input-icon" />
                                            <input className="form-input" placeholder="e.g. Al Ahliya" value={form.insuranceProvider} onChange={e => setForm({ ...form, insuranceProvider: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Policy Number</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.search} size={16} className="input-icon" />
                                            <input className="form-input" placeholder="e.g. POL-123456" value={form.policyNumber} onChange={e => setForm({ ...form, policyNumber: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Insurance Expiry Date</label>
                                        <div className="input-wrapper">
                                            <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" size={16} className="input-icon" />
                                            <input className="form-input" type="date" value={form.insuranceExpiry} onChange={e => setForm({ ...form, insuranceExpiry: e.target.value })} />
                                        </div>
                                    </div>
                                </div>

                                {/* ── Section: Financial Details ── */}
                                <div className="section-label">Financial Details</div>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="form-label">Purchase Price</label>
                                        <div className="input-wrapper">
                                            <Icon d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" size={16} className="input-icon" />
                                            <input className="form-input" type="number" step="0.01" placeholder="0.00" value={form.purchasePrice} onChange={e => setForm({ ...form, purchasePrice: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Acquisition Date</label>
                                        <div className="input-wrapper">
                                            <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" size={16} className="input-icon" />
                                            <input className="form-input" type="date" value={form.acquisitionDate} onChange={e => setForm({ ...form, acquisitionDate: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Depreciation / Month</label>
                                        <div className="input-wrapper">
                                            <Icon d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" size={16} className="input-icon" />
                                            <input className="form-input" type="number" step="0.01" placeholder="0.00" value={form.depreciationMonthly} onChange={e => setForm({ ...form, depreciationMonthly: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">GPS Fee / Month</label>
                                        <div className="input-wrapper">
                                            <Icon d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" size={16} className="input-icon" />
                                            <input className="form-input" type="number" step="0.01" placeholder="0.00" value={form.gpsMonthly} onChange={e => setForm({ ...form, gpsMonthly: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Dashcam Fee / Month</label>
                                        <div className="input-wrapper">
                                            <Icon d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" size={16} className="input-icon" />
                                            <input className="form-input" type="number" step="0.01" placeholder="0.00" value={form.dashcamMonthly} onChange={e => setForm({ ...form, dashcamMonthly: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Insurance / Month</label>
                                        <div className="input-wrapper">
                                            <Icon d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" size={16} className="input-icon" />
                                            <input className="form-input" type="number" step="0.01" placeholder="0.00" value={form.insuranceMonthly} onChange={e => setForm({ ...form, insuranceMonthly: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Other Costs / Month</label>
                                        <div className="input-wrapper">
                                            <Icon d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" size={16} className="input-icon" />
                                            <input className="form-input" type="number" step="0.01" placeholder="0.00" value={form.otherCostsMonthly} onChange={e => setForm({ ...form, otherCostsMonthly: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Monthly Total</label>
                                        <div style={{ padding: '12px 16px', borderRadius: 14, background: 'linear-gradient(135deg,#eef2ff,#e6f0fa)', border: '1.5px solid #c3dcf5', fontWeight: 800, fontSize: 17, color: '#11427D', textAlign: 'center' }}>
                                            {(
                                                (parseFloat(form.depreciationMonthly) || 0) +
                                                (parseFloat(form.gpsMonthly) || 0) +
                                                (parseFloat(form.dashcamMonthly) || 0) +
                                                (parseFloat(form.insuranceMonthly) || 0) +
                                                (parseFloat(form.otherCostsMonthly) || 0)
                                            ).toFixed(2)}
                                        </div>
                                    </div>
                                </div>

                                {/* ── Section: Documents ── */}
                                <div className="section-label">Documents</div>
                                <div style={{ background: '#f8fafc', padding: 20, borderRadius: 16, border: '1px solid #e2e8f0', marginBottom: 20 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                        <label className="form-label" style={{ margin: 0 }}>Vehicle Documents</label>
                                        <button type="button" onClick={handleAddDocument} style={{ background: '#eef2ff', color: '#16539C', border: 'none', padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <Icon d="M12 5v14M5 12h14" /> Add Document
                                        </button>
                                    </div>
                                    
                                    {form.documents?.length > 0 && (
                                        <div style={{ marginBottom: 20 }}>
                                            <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b', marginBottom: 8 }}>Existing Documents</div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                {form.documents.map((doc, idx) => (
                                                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'white', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                                                        <Icon d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" size={18} />
                                                        <span style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{doc.name || 'Document'}</span>
                                                        <a href={`/api/uploads/${doc.file}`} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 'auto', fontSize: 12, color: '#16539C', textDecoration: 'none', fontWeight: 600 }}>View</a>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                        {documentsList.map((doc, index) => (
                                            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                <input className="form-input" placeholder="Doc Name" value={doc.name} onChange={e => handleDocumentChange(index, 'name', e.target.value)} style={{ flex: 1 }} />
                                                <input className="form-input" type="file" onChange={e => handleDocumentChange(index, 'file', e.target.files[0])} style={{ flex: 1, padding: '9px 16px' }} />
                                                {documentsList.length > 1 && (
                                                    <button type="button" onClick={() => handleRemoveDocument(index)} style={{ background: '#fee2e2', color: '#ef4444', border: 'none', padding: 10, borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Icon d={icons.trash} />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="cancel-btn" onClick={closeModal}>Discard</button>
                                <button type="submit" className="submit-btn">{editingId ? 'Update Vehicle' : 'Register Vehicle'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

function VendorFleetImportWizard({ operatorId, vendors, onBack, onDone }) {
    const [file, setFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [parsedData, setParsedData] = useState([]);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    const handleDrag = (e) => {
        e.preventDefault(); e.stopPropagation();
        setDragActive(e.type === 'dragenter' || e.type === 'dragover');
    };
    const handleDrop = (e) => {
        e.preventDefault(); e.stopPropagation(); setDragActive(false);
        if (e.dataTransfer.files?.[0]) processFile(e.dataTransfer.files[0]);
    };
    const handleFileChange = (e) => { if (e.target.files?.[0]) processFile(e.target.files[0]); };

    const processFile = (f) => {
        setFile(f);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const wb = XLSX.read(e.target.result, { type: 'binary' });
                const data = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
                setParsedData(data.map(row => Object.fromEntries(Object.keys(row).map(k => [k.trim(), row[k]]))));
            } catch { alert('Failed to parse Excel file.'); }
        };
        reader.readAsBinaryString(f);
    };

    const downloadTemplate = () => {
        const ws = XLSX.utils.json_to_sheet([{
            'Vehicle ID / Code': 'VF-001',
            'Vendor Name': 'ACME Logistics',
            'Registration Number': 'DXB-12345',
            'Vehicle Type': 'Bus',
            'Make': 'Toyota',
            'Model': 'Coaster',
            'Year': 2022,
            'Fuel Type': 'Diesel',
            'Color': 'White',
            'Purchase Price': 120000,
            'Acquisition Date': '2022-01-15',
            'Depreciation Monthly': 500,
            'GPS Monthly': 100,
            'Dashcam Monthly': 80,
            'Insurance Monthly': 300,
            'Other Costs Monthly': 50,
            'Status': 'Active'
        }]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Vendor Fleet');
        XLSX.writeFile(wb, 'VendorFleet_Template.xlsx');
    };

    const handleImport = async () => {
        if (!parsedData.length) return;
        setImporting(true);
        try {
            const mapped = parsedData.map(r => {
                const vendor = vendors.find(v => v.vendorName?.toLowerCase() === (r['Vendor Name'] || '').toLowerCase());
                return {
                    vendorId: vendor?._id || null,
                    vehicleCode: r['Vehicle ID / Code'] || r['Vehicle Code'] || r['Vehicle ID'] || r['vehicleCode'] || '',
                    vendorName: r['Vendor Name'],
                    registrationNumber: r['Registration Number'],
                    vehicleType: r['Vehicle Type'] || '',
                    make: r['Make'] || '',
                    model: r['Model'] || '',
                    year: r['Year'] || new Date().getFullYear(),
                    fuelType: r['Fuel Type'] || '',
                    color: r['Color'] || '',
                    purchasePrice: r['Purchase Price'] || 0,
                    acquisitionDate: r['Acquisition Date'] || '',
                    depreciationMonthly: r['Depreciation Monthly'] || 0,
                    gpsMonthly: r['GPS Monthly'] || 0,
                    dashcamMonthly: r['Dashcam Monthly'] || 0,
                    insuranceMonthly: r['Insurance Monthly'] || 0,
                    otherCostsMonthly: r['Other Costs Monthly'] || 0,
                    status: r['Status'] || 'Active',
                    operatorId
                };
            });
            const res = await fetch('/api/vendor-fleet/import', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ operatorId, vehicles: mapped })
            });
            const data = await res.json();
            if (res.ok) setResult({ createdCount: data.successCount, errors: data.errors || [] });
            else alert(data.message || 'Import failed');
        } catch { alert('Import failed.'); }
        finally { setImporting(false); }
    };

    const S = { page: { padding: 30, background: '#f8fafc', minHeight: '100vh', fontFamily: "'Inter',sans-serif" } };

    if (result) return (
        <div style={S.page}>
            <div style={{ background: 'white', borderRadius: 16, padding: 40, textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9', maxWidth: 700, margin: '0 auto' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 80, height: 80, borderRadius: '50%', background: '#d1fae5', color: '#10b981', marginBottom: 24 }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <h2 style={{ fontSize: 28, fontWeight: 800, color: '#1e293b', marginBottom: 12 }}>Import Completed!</h2>
                <p style={{ fontSize: 14, color: '#64748b', marginBottom: 24 }}>Vehicles matched to vendors by <strong>Vendor Name</strong>.</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, maxWidth: 500, margin: '0 auto 30px' }}>
                    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: 20, textAlign: 'center' }}>
                        <div style={{ fontSize: 32, fontWeight: 800, color: '#1e40af' }}>{result.createdCount || 0}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1d4ed8', marginTop: 4 }}>Vehicles Added</div>
                    </div>
                    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: 20, textAlign: 'center' }}>
                        <div style={{ fontSize: 32, fontWeight: 800, color: '#92400e' }}>{result.errors?.length || 0}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#b45309', marginTop: 4 }}>Skipped / Errors</div>
                    </div>
                </div>
                {result.errors?.length > 0 && (
                    <div style={{ textAlign: 'left', background: '#fef2f2', padding: 16, borderRadius: 8, marginBottom: 24, maxHeight: 140, overflowY: 'auto' }}>
                        <h4 style={{ margin: '0 0 8px', color: '#b91c1c', fontSize: 14 }}>Errors:</h4>
                        <ul style={{ margin: 0, paddingLeft: 20, color: '#991b1b', fontSize: 13 }}>
                            {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                        </ul>
                    </div>
                )}
                <button onClick={onDone} style={{ padding: '12px 30px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>Return to Fleet</button>
            </div>
        </div>
    );

    return (
        <div style={S.page}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 30 }}>
                <button onClick={onBack} style={{ width: 36, height: 36, borderRadius: 10, border: '1px solid #e2e8f0', background: 'white', color: '#64748b', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                </button>
                <div>
                    <h1 style={{ fontSize: 24, fontWeight: 800, color: '#1e293b', margin: 0 }}>Import Vendor Fleet</h1>
                    <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>Bulk import fleet vehicles via Excel. Vendors matched by <strong>Vendor Name</strong>.</p>
                </div>
            </div>

            {parsedData.length === 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: 30 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                        <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', padding: 24 }}>
                            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>ℹ️ How it works</h3>
                            <p style={{ fontSize: 13, color: '#475569', marginBottom: 12, lineHeight: 1.6 }}>
                                Vehicles are linked to an existing vendor by matching the <strong>Vendor Name</strong> column exactly.
                                If no match is found the row is still saved without a vendor link.
                            </p>
                            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 12 }}>Supported Fields</h3>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {['Vehicle ID / Code', 'Vendor Name','Registration Number','Vehicle Type','Make','Model','Year','Fuel Type','Color','Purchase Price','Acquisition Date','Depreciation Monthly','GPS Monthly','Dashcam Monthly','Insurance Monthly','Other Costs Monthly','Status'].map((f,i) => (
                                    <span key={i} style={{ fontSize: 11, fontWeight: 600, color: '#11427D', background: '#eef2ff', border: '1px solid #c3dcf5', padding: '4px 8px', borderRadius: 6 }}>{f}</span>
                                ))}
                            </div>
                        </div>
                        <div style={{ background: '#f8fafc', borderRadius: 16, border: '1px dashed #cbd5e1', padding: 20, textAlign: 'center' }}>
                            <p style={{ fontSize: 13, color: '#475569', margin: '0 0 12px' }}>Download a pre-filled sample template.</p>
                            <button onClick={downloadTemplate} style={{ padding: '8px 16px', borderRadius: 8, border: '1.5px solid #cbd5e1', background: 'white', color: '#334155', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Download Template</button>
                        </div>
                    </div>
                    <div onDragEnter={handleDrag} onDragOver={handleDrag} onDragLeave={handleDrag} onDrop={handleDrop}
                        onClick={() => document.getElementById('vf-file-input').click()}
                        style={{ background: dragActive ? '#f0f7ff' : 'white', borderRadius: 16, border: `2px dashed ${dragActive ? '#16539C' : '#cbd5e1'}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 40px', cursor: 'pointer', transition: 'all 0.15s' }}>
                        <input id="vf-file-input" type="file" accept=".xlsx,.xls" onChange={handleFileChange} style={{ display: 'none' }}/>
                        <div style={{ width: 64, height: 64, borderRadius: 16, background: '#eef2ff', color: '#16539C', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                        </div>
                        <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: '0 0 8px' }}>Drag & Drop Excel File</h3>
                        <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px' }}>or click to browse your local files</p>
                        <span style={{ fontSize: 12, color: '#94a3b8', background: '#f8fafc', padding: '6px 12px', borderRadius: 6, border: '1px solid #f1f5f9' }}>Supports .xlsx, .xls formats</span>
                    </div>
                </div>
            ) : (
                <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>{file?.name}</span>
                            <span style={{ fontSize: 12, color: '#64748b', background: '#cbd5e1', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>{parsedData.length} Rows</span>
                        </div>
                        <div style={{ display: 'flex', gap: 12 }}>
                            <button onClick={() => { setFile(null); setParsedData([]); }} style={{ padding: '9px 18px', borderRadius: 10, border: '1.5px solid #cbd5e1', background: 'white', color: '#475569', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Change File</button>
                            <button onClick={handleImport} disabled={importing} style={{ padding: '9px 22px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{importing ? 'Processing...' : `Proceed Import (${parsedData.length})`}</button>
                        </div>
                    </div>
                    <div style={{ overflowX: 'auto', maxHeight: 500 }}>
                        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                            <thead><tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                {['Vendor Name','Registration Number','Make / Model','Status'].map(h => <th key={h} style={{ padding: '14px 20px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>{h}</th>)}
                            </tr></thead>
                            <tbody>
                                {parsedData.map((d, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '14px 20px', fontWeight: 700, color: '#1e293b' }}>{d['Vendor Name'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: 13 }}>{d['Registration Number'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: 13 }}>{d['Make'] || ''} {d['Model'] || ''}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: 13 }}>{d['Status'] || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
